/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import type { BarSeriesData } from '#UNSAFE_BarChart/barChart.types';
import type { LineAreaSeriesData } from '#UNSAFE_LineAreaChart/lineAreaChart.types';
import type { RefObject } from 'preact';
import type {
  ChartContextMenuContext,
  ChartContextMenuConfig,
  Info
} from '#utils/UNSAFE_visTypes/chart';
import { type gestureTypes, useContextMenu } from '#hooks/PRIVATE_useVisContextMenu';
import { getChartContextMenuContext } from './contextMenuUtils';
import { calculateOffset } from '#utils/PRIVATE_visLayoutUtils/layoutUtils';
import { Dimension } from '#utils/UNSAFE_visTypes/common';
import { GroupLevelItem } from '#PRIVATE_Axis/groupAxis/hierarchicalUtils';

type Props<K, D> = {
  series: (BarSeriesData<K, D> | LineAreaSeriesData<K, D>)[];
  levelsArray: GroupLevelItem[][];
  isRtl: boolean;
  width: number;
  height: number;
  getDataItemPosition: (seriesIndex: number, groupIndex: number) => Dimension | undefined;
  getDataItem: (seriesIndex: number, groupIndex: number, groupPath: number[]) => D | undefined;
  focusedItemInfo: Info;
  focusedGroupInfo: Info;
  rootRef: RefObject<HTMLDivElement>;
  xAxisTitle?: string;
  yAxisTitle?: string;
  contextMenuConfig?: ChartContextMenuConfig<K, D>;
  onContextMenuDismissed?: (gesture: gestureTypes) => void;
};

export const useChartContextMenu = <K extends string | number, D>({
  isRtl,
  width,
  getDataItemPosition,
  getDataItem,
  focusedItemInfo,
  focusedGroupInfo,
  series,
  levelsArray,
  rootRef,
  xAxisTitle,
  yAxisTitle,
  contextMenuConfig,
  onContextMenuDismissed
}: Props<K, D>) => {
  const { contextMenuProps, contextMenuContent } = useContextMenu<ChartContextMenuContext<K, D>>({
    onContextMenuHandler: ({ gesture, target }) => {
      const { context } = getChartContextMenuContext<K, D>(
        focusedItemInfo,
        focusedGroupInfo,
        gesture,
        target as HTMLElement,
        series,
        levelsArray,
        rootRef,
        xAxisTitle,
        yAxisTitle,
        getDataItem
      );
      if (gesture === 'keyboard' && context.type === 'item') {
        const elementBounds = focusedItemInfo?.isCurrent
          ? getDataItemPosition(focusedItemInfo.seriesIndex!, focusedItemInfo.groupIndex!)
          : undefined;
        const offsetValue = calculateOffset(isRtl, width, elementBounds);
        return {
          type: 'offset',
          context,
          offsetValue,
          isRtl,
          width
        };
      }
      return { type: 'pointer', context };
    },
    contextMenuOptions: {
      isDisabled: !contextMenuConfig //If there is not a context menu renderer we disable the hook
    },
    rootRef,
    contextMenuConfig,
    onContextMenuDismissed
  });

  return { contextMenuContent, contextMenuProps };
};
